import { FinanceInvestigationKind } from "@ibada/shared-types/finance"

import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"

import type { FinanceInvestigationEventsParams } from "@/features/finance/services/finance-investigation-api"

/**
 * The one investigation entry-point contract (IBA-75). Every "Investigate" button anywhere in Finance
 * builds an {@link InvestigationContext} and hands it to {@link buildFinanceInvestigationHref}. A context
 * that names a single object (a transaction or a journal entry) deep-links to the full investigation
 * WORKSPACE; a context that names a scope (an account, fund, budget line, activity, actor, reference,
 * amount or date range) deep-links to the Explorer, prefiltered. No screen re-implements audit logic.
 */
export type InvestigationContext = {
  // Single-object anchors → the investigation workspace.
  transactionId?: string
  journalEntryId?: string
  // Scope facets → the prefiltered Explorer.
  accountingAccountId?: string
  fundId?: string
  activityId?: string
  budgetLineId?: string
  incomeCategoryId?: string
  expenseCategoryId?: string
  programId?: string
  memberId?: string
  ministryId?: string
  groupId?: string
  departmentId?: string
  actorUserId?: string
  transactionReference?: string
  type?: string
  status?: string
  reconciled?: boolean
  hasEvidence?: boolean
  amountFrom?: number
  amountTo?: number
  dateFrom?: string
  dateTo?: string
  /** Free-text search across reference / description / category / activity. */
  search?: string
  /** Human label for the leading Explorer chip (e.g. an account or fund name). Display only. */
  label?: string
}

/** Explorer query-param keys carried on the `/finance/investigations` URL (source of truth for the events query). */
const EXPLORER_KEYS = [
  "accountingAccountId",
  "fundId",
  "activityId",
  "budgetLineId",
  "incomeCategoryId",
  "expenseCategoryId",
  "programId",
  "memberId",
  "ministryId",
  "groupId",
  "departmentId",
  "actorUserId",
  "transactionReference",
  "amountFrom",
  "amountTo",
  "dateFrom",
  "dateTo",
  "type",
  "status",
  "reconciled",
  "hasEvidence",
  "search",
] as const

type ExplorerKey = (typeof EXPLORER_KEYS)[number]

type BuildHrefOptions = {
  workspaceSlug?: string | null
  branchSlug?: string | null
  /** Finance context to preserve (`?context=parish`); mirrors the existing finance-tab convention. */
  financeContext?: "branch" | "parish"
}

const withContext = (query: URLSearchParams, financeContext?: "branch" | "parish") => {
  if (financeContext === "parish") query.set("context", "parish")
  const s = query.toString()
  return s ? `?${s}` : ""
}

/** Build the deep-link href for an investigation context. */
export function buildFinanceInvestigationHref(context: InvestigationContext, options: BuildHrefOptions): string {
  const { workspaceSlug, branchSlug, financeContext } = options

  // Single object → the workspace full page.
  if (context.transactionId) {
    const query = withContext(new URLSearchParams(), financeContext)
    return buildBranchAdminUrl(
      branchSlug,
      `/finance/investigations/investigation/${FinanceInvestigationKind.Transaction}/${context.transactionId}${query}`,
      workspaceSlug,
    )
  }
  if (context.journalEntryId) {
    const query = withContext(new URLSearchParams(), financeContext)
    return buildBranchAdminUrl(
      branchSlug,
      `/finance/investigations/investigation/${FinanceInvestigationKind.Journal}/${context.journalEntryId}${query}`,
      workspaceSlug,
    )
  }

  // Scope → the prefiltered Explorer.
  const query = new URLSearchParams()
  const set = (key: string, value: string | number | undefined | null) => {
    if (value !== undefined && value !== null && value !== "") query.set(key, String(value))
  }
  set("accountingAccountId", context.accountingAccountId)
  set("fundId", context.fundId)
  set("activityId", context.activityId)
  set("budgetLineId", context.budgetLineId)
  set("incomeCategoryId", context.incomeCategoryId)
  set("expenseCategoryId", context.expenseCategoryId)
  set("programId", context.programId)
  set("memberId", context.memberId)
  set("ministryId", context.ministryId)
  set("groupId", context.groupId)
  set("departmentId", context.departmentId)
  set("actorUserId", context.actorUserId)
  set("transactionReference", context.transactionReference)
  set("type", context.type)
  set("status", context.status)
  if (typeof context.reconciled === "boolean") set("reconciled", String(context.reconciled))
  if (typeof context.hasEvidence === "boolean") set("hasEvidence", String(context.hasEvidence))
  set("amountFrom", context.amountFrom)
  set("amountTo", context.amountTo)
  set("dateFrom", context.dateFrom)
  set("dateTo", context.dateTo)
  set("search", context.search)
  set("label", context.label)
  return buildBranchAdminUrl(branchSlug, `/finance/investigations${withContext(query, financeContext)}`, workspaceSlug)
}

/** True when the audit-page URL carries any Explorer prefilter (→ show the Explorer, not the Launcher). */
export function hasInvestigationPrefilter(searchParams: URLSearchParams): boolean {
  return EXPLORER_KEYS.some((key) => Boolean(searchParams.get(key)))
}

/** Read the Explorer facets off the audit-page URL into events-query params (minus scope/workspace). */
export function parseExplorerParams(searchParams: URLSearchParams): Partial<FinanceInvestigationEventsParams> {
  const out: Partial<FinanceInvestigationEventsParams> = {}
  const read = (key: ExplorerKey) => searchParams.get(key) ?? undefined
  const str = read("accountingAccountId")
  if (str) out.accountingAccountId = str
  const fund = read("fundId")
  if (fund) out.fundId = fund
  const activity = read("activityId")
  if (activity) out.activityId = activity
  const budgetLine = read("budgetLineId")
  if (budgetLine) out.budgetLineId = budgetLine
  const incomeCategory = read("incomeCategoryId")
  if (incomeCategory) out.incomeCategoryId = incomeCategory
  const expenseCategory = read("expenseCategoryId")
  if (expenseCategory) out.expenseCategoryId = expenseCategory
  const program = read("programId")
  if (program) out.programId = program
  const member = read("memberId")
  if (member) out.memberId = member
  const ministry = read("ministryId")
  if (ministry) out.ministryId = ministry
  const group = read("groupId")
  if (group) out.groupId = group
  const department = read("departmentId")
  if (department) out.departmentId = department
  const actor = read("actorUserId")
  if (actor) out.actorUserId = actor
  const reference = read("transactionReference")
  if (reference) out.transactionReference = reference
  const type = read("type")
  if (type) out.type = type
  const status = read("status")
  if (status) out.status = status
  const reconciled = read("reconciled")
  if (reconciled === "true" || reconciled === "false") out.reconciled = reconciled === "true"
  const hasEvidence = read("hasEvidence")
  if (hasEvidence === "true" || hasEvidence === "false") out.hasEvidence = hasEvidence === "true"
  const amountFrom = read("amountFrom")
  if (amountFrom) out.amountFrom = Number(amountFrom)
  const amountTo = read("amountTo")
  if (amountTo) out.amountTo = Number(amountTo)
  const dateFrom = read("dateFrom")
  if (dateFrom) out.dateFrom = dateFrom
  const dateTo = read("dateTo")
  if (dateTo) out.dateTo = dateTo
  const search = read("search")
  if (search) out.search = search
  return out
}
